<template>
  <div class='wrapper-page message-show'>
    <modal class="filter-all-messages" ref="deleteMessage">
      <template #header>
        <h5 class='modal-title mt-0'>
          {{ $t('communication.delete_messages') }}
        </h5>
      </template>
      <template #body>
        <form>
          <div class='modal-body'>
            <p>{{ $t('communication.are_you_sure_you_wanna_delete') }}</p>
          </div>
          <div class='modal-footer'>
            <input :value="$t('communication.delete_messages_button')" @click.prevent='deleteMessage(message)'
                   class='btn btn-outline-danger float-right' type='reset'/>
          </div>
        </form>
      </template>
    </modal>

    <modal ref="replyRecordMessageModal">
      <template #header>
        <h5 class='modal-title mt-0'>
          {{ (Object.keys(selectedReplyMessage).length > 0) ? $t('communication.modals.reply') : $t('communication.modals.reply_all')}}
        </h5>
      </template>
      <template #body>
        <div class='modal-body'>
          <reply-record-message
            :message="selectedMessage"
            :replyMessage="selectedReplyMessage"
            :addressBook="addressBook"
            resourceType='RecordMessageShow'
            @success="closeRecordMessage()" />
        </div>
      </template>
    </modal>

    <modal ref="forwardRecordMessageModal">
      <template #header>
        <h5 class='modal-title mt-0'>
          {{ $t('communication.forward')}}
        </h5>
      </template>
      <template #body>
        <div class='modal-body'>
          <forward-record-message
            :message="selectedMessage"
            :addressBook="addressBook"
            resourceType='RecordMessageShow'
            @success="closeForwardMessage()" />
        </div>
      </template>
    </modal>

    <div class='container-fluid'>
      <p class="back-to-list">
        <router-link :to="backTo">
          <i class="fas fa-long-arrow-alt-left"></i>
          {{ $t('communication.back_to')}} {{ referrerName }}
        </router-link>
      </p>
      <div class="persf" v-if="!isCurrent || message.deleted">
          <i :class="['fa-star', {'far': !message.starred, 'fas': message.starred }]"></i>
      </div>
      <div class="persf" v-else>
        <a @click.prevent="toggleStarMessage(message)" href='#'>
          <i :class="['fa-star', {'far': !message.starred, 'fas': message.starred }]"></i>
        </a>
      </div>
      <h1 class='page-title'>
        <p>
          {{ $t('record_message.sent_to_label')}}: {{ sentTo(message) }}
        </p>
      </h1>
      <div class="file-wrapper">
        <label>{{ $t('communication.related_file') }}</label>
        <div class="related-file">
        <img :src="mimeToSrc(record.mime_type)"/>
        <router-link v-if="record.id==current_version.id" :to="{name: 'record', hash: '#messages', params: { id: record.id, propRecords: [] }}" class="text-truncate file-name">
          {{ record.filename }}
        </router-link>
        <router-link v-else :to="{ name: 'record_show_versioned', params: { record_id: current_version.id, id: record.id } }">
          {{ message.record.filename }}
        </router-link>

        </div>
      </div>
      <div class="message-tags-wrapper" v-if="!message.deleted">
        <label>{{ $t('communication.message_show.tags') }}</label>
        <message-tag-manager :message="message" message-type="recordMessage" @success='getMessage()' />
      </div>
      <loading v-if="loading" />
      <section class="message-wrapper" v-else>
        <header>
          <div class="sender-info" v-if="message.from_user">
            <user-image :user='message.from_user' />
            <span>{{ message.from_user.full_name }}</span>
          </div>

          <div class="dropdown">
            <p class="date">
              <strong>{{ $t('communication.created_on') }}</strong>
              <span>
                {{ formatTime(message.created_at) }}
                {{ formatDate(message.created_at) }}
              </span>
            </p>
            <button v-if="!message.deleted" aria-expanded="false" aria-haspopup="true" class="btn dropdown-toggle" data-toggle="dropdown"
                    type="button">· · ·
            </button>
            <div class="dropdown-menu dropdown-menu-right" v-if="!message.deleted">
              <ul>
                <li v-if="isCurrent">
                  <a href="#" @click.prevent="showReplyForm(message, {})" class="dropdown-item">
                    <i class="fas fa-reply-all" />
                    {{ $t('record_message.reply_all') }}
                  </a>
                </li>
                <li>
                  <a v-if="isCurrent"
                     href="#" @click.prevent="showForwardRecordMessage(message)" class="dropdown-item">
                    <i class="fas fa-arrow-right" />
                    {{ $t('communication.forward') }}
                  </a>
                </li>
                <li>
                  <a v-if="!message.deleted" @click.prevent='$refs.deleteMessage.open()' class="dropdown-item" href="#" rel="nofollow">
                    <i class="far fa-trash-alt"></i>
                    {{ $t('communication.delete') }}
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </header>
        <div class="message-body">
          <p v-html="message.body"></p>
          <message-reactions :readonly="!isCurrent || message.deleted" :message="message" messageType='' :users="users" @success="getMessage()" />
        </div>
      </section>


      <section class="message-wrapper" v-for="reply in replies">
        <header>
          <div class="sender-info">
            <div v-if="reply.from_user !== undefined" class="img-usr">
              <user-image :user='reply.from_user'/>
              <span>{{ reply.from_user.full_name }}</span>
            </div>
            <div v-else-if="reply.from_contact" class="img-usr">
              <div class="contact-image-wrapper rounded-circle">
                <span>
                  {{ reply.from_contact.name ? reply.from_contact.name.substring(0, 1) : '' }}
                </span>
              </div>
              <span>{{ reply.from_contact.name }}</span>
            </div>
          </div>

          <div class="date">
            <p>
              <strong>{{ $t('communication.created_on') }}</strong>
              <span>
                {{ formatTime(reply.created_at) }}
                {{ formatDate(reply.created_at) }}
              </span>
            </p>
            <p>
              {{ $t('record_message.sent_to_label')}}: {{ replySentTo(reply) }}
            </p>
            <p>
              <strong>{{ $t('record_message.sent_via') }}:</strong>
              <span>{{ repliedVia(reply.sent_via) }}</span>
            </p>
          </div>
          <a href="#" v-if="!message.deleted" @click.prevent="showReplyForm(message, reply)">
            <i class="fas fa-reply"></i>
            {{$t('record_message.reply')}}
          </a>
        </header>
        <div class="message-body">
          <p v-show="reply.forwarded">--- {{ $t('record_message.forwarded_message') }} ---<p>
          <p v-html="reply.body"></p>
          <message-reactions :readonly="!isCurrent || message.deleted" :message="reply" messageType='reply' :users="users" @success="getMessage()" />
        </div>
      </section>

    </div>
  </div>
</template>

<script>
import _modal from "../../components/_modal";
import message_tag_manager from "../../components/tags/_message_tag_manager"
import _user_image from '../../components/_user_image'
import _reply_record_message from '../../components/communications/_reply_record_message'
import _forward_record_message from '../../components/communications/_forward_record_message_form'
import _message_reactions from '../../components/communications/_message_reactions'
import _loading from '../../components/_loading'

export default {
  name: 'MessageShow',
  components: {
    'modal': _modal,
    'message-tag-manager': message_tag_manager,
    'user-image': _user_image,
    'reply-record-message': _reply_record_message,
    'forward-record-message': _forward_record_message,
    'message-reactions': _message_reactions,
    'loading': _loading,
  },
  data() {
    return {
      loading: true,
      selectedMessage: {},
      selectedReplyMessage: {},
      recordMessageBody: '',
      selectedAddressBook: [],
      cleanup: false,
    }
  },
  props: {
    id: {
      required: true
    },
    hash: {},
    referrer: {
      type: String,
      required: false
    }
  },
  created() {
    this.getMessage()
    this.listStore.getUsers()
    this.store.sendMetrics({name: 'file_communication_show', params: this.$route.params, path: this.$route.fullPath})
  },
  computed: {
    message() {
      return this.state.recordMessage || {}
    },
    record() {
      return this.message.record || {}
    },
    current_version() {
      return this.record.current_version || {}
    },
    replies() {
      let replies = this.message.replies || []
      //the oldest to the newest
      return replies.slice(0).sort((a, b) => a.created_at.localeCompare(b.created_at)) || []
    },
    isCurrent(){
      return this.record.id == this.current_version.id
    },
    referrerName(){
      let referrer = this.$props.referrer || null
      let type = this.query.type || null

      if(referrer === 'trash' || type === 'trash')
        return this.$t('communication.trash')
      else if(referrer === 'tag-messages' || type === 'tag-messages')
        return this.$t('communication.tags')
      else
        return this.$t('communication.interactive_notes')
    },
    users() {
      return this.lists.users || []
    },
    backTo(){
      let referrer = this.$props.referrer || null
      let type = this.query.type || null
      let type_id = this.query.type_id || null

      if(referrer === 'trash' || type === 'trash')
        return { name: 'trash' }
      else if(referrer === 'tag-messages' || type === 'tag-messages')
        return { name: 'tag', hash: '#messages', params: { id: type_id } }
      else
        return { name: 'record_message' }
    },
    addressBook() {
      return (this.lists.addressBook || []).map( (item) => { return { id: item.id, name: item.name } }, [])
    },
    recordMessageStats() {
      return this.stats.recordMessageStats || []
    },
    repliesStats(){
      return this.recordMessageStats.replies || []
    },
    newMessageStats(){
      return this.recordMessageStats.new_messages || []
    }
  },

  methods: {
    getMessage() {
      this.loading = false
      if(this.$route.params.id){
        this.store.showCommunicationRecordMessage(this.$route.params.id).finally(() => {
          this.loading = false
          this.store.seenRecordMessage(this.record.id, this.$route.params.id).finally(() => {
            this.loadStatsRecordMessage()
          })
        })
      }else{
        this.$router.push({name: 'record_message', params: {}})
      }
    },
    reactionsCount(reactions, name){
      if(reactions === undefined)
          return 0


      let user_ids = reactions[name] || []
      let count = user_ids.length
      if(count > 0)
          return user_ids.length
      else
          return ''
    },
    react(message_id, name, type=''){
      this.store.updateMessageReactions(message_id, { name: name, type: type }).then(({}) => {
        this.store.sendMetrics({name: 'file_communication_show_react_to_message',params: this.$route.params,path: this.$route.fullPath})
        this.getMessage()
      })
    },
    loadStatsRecordMessage(){
      this.statStore.getCommunicationMessagesStats({ 'filters[record_message_id]': this.$props.id, 'filters[to_user_id]': this.currentUser.id })
      this.statStore.getCommunicationMessagesStats({ 'filters[to_user_id]': this.currentUser.id }, true)
    },
    toggleStarMessage(message) {
      message.starred = !message.starred
      return this.store.updateRecordMessage(message.record.id, message.id, { record_message_sent: {user_ids: [], contact_ids: []}, record_message: { starred: message.starred}}).then(() => {
      this.store.sendMetrics({name: 'file_communication_show_star_message', params: this.$route.params, path: this.$route.fullPath})
        this.getMessage()
      })
    },
    repliedVia(via){
      switch (via) {
        case 'app':
          return this.$t('record_message.replied_from_app')
        case 'email':
          return this.$t('record_message.replied_from_email')
        case 'public_page':
          return this.$t('record_message.replied_from_public_page')
      }
    },
    sentTo(message){
        return this.unique(
                 this.sentMessageToNames(message).flat()
                 .concat(this.sentForwardings(message).flat())
               ).join(', ')
    },
    replySentTo(reply_message){
      let currentOrg = this.currentOrg
      let sent_messages =  reply_message.sent_messages || []
      return sent_messages.map( (item) => {
        if(item.to_user !== undefined){
          if(item.to_user.organization_id == currentOrg.id)
            return item.to_user.full_name
        }
        if(item.to_contact !== undefined){
          if(item.to_contact.organization_id == currentOrg.id)
            return item.to_contact.name
        }
      }, []).join(', ')
    },
    sentMessageToNames(message){
      let currentOrg = this.currentOrg
      let sent_messages =  message.sent_messages || []
      return sent_messages.map( (item) => {
        if(item.to_user !== undefined){
          if(item.to_user.organization_id == currentOrg.id)
            return item.to_user.full_name
        }
        if(item.to_contact !== undefined){
          if(item.to_contact.organization_id == currentOrg.id)
            return item.to_contact.name
        }
      }, [])
    },
    sentForwardings(message){
      let currentOrg = this.currentOrg
      let replies =  message.replies || []
      return replies.map( (item) => {

         let sent_messages = item.sent_messages || []
         return sent_messages.map( (sent_message) => {
             let to_user = sent_message.to_user || null
             let to_contact = sent_message.to_contact || null

           if(to_user){
             if(sent_message.to_user.organization_id == currentOrg.id)
               return to_user.full_name
           }
           if(to_contact){
             if(to_contact.organization_id == currentOrg.id)
               return to_contact.name
           }
         }, [])
      }, [])
    },
    unique(array){
      return array.filter((v, i, a) => a.indexOf(v) === i)
    },
    showReplyForm(message, replyMessage){
      this.listStore.getAddressBook().finally(() => {
        this.selectedMessage = message
        this.selectedReplyMessage = replyMessage
        this.recordMessageBody = ''

        this.$refs.replyRecordMessageModal.open()
        this.store.sendMetrics({name: 'file_communication_show_open_reply_modal', params: this.$route.params, path: this.$route.fullPath})
      })
    },
    showForwardRecordMessage(message){
      this.listStore.getAddressBook()
      this.$refs.forwardRecordMessageModal.open()
      this.selectedMessage = message
      this.recordMessageBody = message.body

      this.store.sendMetrics({name: 'file_communication_show_open_forward_modal', params: this.$route.params, path: this.$route.fullPath})
    },
    closeRecordMessage(){
      this.selectedMessage = {}
      this.selectedReplyMessage = {}
      this.$refs.replyRecordMessageModal.close()
      this.getMessage()
    },
    closeForwardMessage(){
      this.selectedMessage = {}
      this.selectedReplyMessage = {}
      this.$refs.forwardRecordMessageModal.close()
      this.getMessage()
    },
    deleteMessage(message) {
      this.$refs.deleteMessage.close()
      this.loading = true
      this.store.deleteRecordMessage([message.id]).then(() => {
        this.getMessage()
        this.loading = false
        this.store.sendMetrics({name: 'file_communication_show_delete_message', params: this.$route.params, path: this.$route.fullPath})
        this.$router.push({name: 'record_message', params: {}})
      })
    }
  }

}
</script>

<style lang="sass" scoped>
@import "../../styles/messages"
.emo, .emo i
  color: #686868 !important
header
  display: flex
  align-items: center
  .dropdown
    margin-left: auto!important
    p.date
      float: none!important
      display: inline
  .sender-info
    display: flex
    align-items: center
    margin-bottom: 10px
    .img-usr
      display: flex
      align-items: center
  .date
    margin-left: auto
    margin-right: 10px
.fa-grin
  color: #f6a623
.fa-thumbs-up
  color: #000E5B

.contact-image-wrapper
  width: 45px
  height: 45px
  line-height: 45px
  background: #56DAE2
  font-weight: bold
  text-align: center
  overflow: hidden
  display: block
  float: left
  color: #fff
  margin-right: 7px
  img
    vertical-align: top
    min-width: 100%
    min-height: 100%
    width: auto
    height: auto
  div
    border-radius: 50%
  &.org-pic
    background: #eee
    color: #000

.file-wrapper
  float: left
  width: 100%
  text-align: left
  display: flex
  align-items: center
  label
    margin: 0 10px 0 0
  .related-file
    float: left
    text-align: left
    display: block
    clear: both
    align-items: center
    border: 1px solid #E6E6E6
    border-radius: 3px
    padding: 5px
    img
      width: 20px
      margin-right: 10px

</style>
